import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  clearPendingSubscriptionCheckout,
  isPendingSubscriptionCheckoutExpired,
  type PendingSubscriptionCheckout,
  readPendingSubscriptionCheckout,
  useUserBalanceStore,
} from '@/entities/balance';
import { useCurrentUser } from '@/entities/user/currentUser';

import { API } from '@/shared/api';
import { type IGetLatestPaymentInfoResponse } from '@/shared/api/billing/types';
import { PATHS } from '@/shared/constants/routes/paths';
import {
  PAYMENT_CREDITS_CANCELED_PARAM,
  PAYMENT_SUBSCRIPTION_CANCELED_PARAM,
  PAYMENT_SUCCESSFUL_PARAM,
  PRICE_ID_PARAM,
} from '@/shared/constants/routes/searchParams';
import { useExecuteOneTime } from '@/shared/hooks/useExecuteOneTime';
import { clearCheckoutEntryPoint, readCheckoutEntryPoint } from '@/shared/lib/checkoutEntryPoint';
import {
  clearRecordingCheckoutReturn,
  getRecordingCheckoutBackPath,
  markRecordingCheckoutReturnPaid,
  takeCanceledRecordingCheckoutReturn,
} from '@/shared/lib/recordingCheckoutReturn';
import { getPlanPeriodLabel } from '@/shared/lib/subscription';
import { GTMEvents } from '@/shared/thirdParty/gtm';
import { MixpanelEvents } from '@/shared/thirdParty/mixpanel';
import { PaymentInterval } from '@/shared/types/Billing';

import { getLatestPaymentData } from './utils/getLatestPaymentData';

const STRIPE_REDIRECT_PARAMS = [
  'payment_intent',
  'payment_intent_client_secret',
  'redirect_pm_type',
  'redirect_status',
];

const stripStripeRedirectParams = (params: URLSearchParams) => STRIPE_REDIRECT_PARAMS.forEach((p) => params.delete(p));

const isSubscriptionPayment = (latestPaymentPeriod: string) =>
  latestPaymentPeriod === 'Annual' || latestPaymentPeriod === 'Monthly';

const getLatestPaymentInterval = (latestPaymentPeriod: string) => {
  switch (latestPaymentPeriod) {
    case 'Annual': {
      return PaymentInterval.Year;
    }
    case 'Monthly': {
      return PaymentInterval.Month;
    }
    default: {
      return null;
    }
  }
};

const matchesPendingSubscriptionCheckout = (
  latestPaymentInfo: { items: Array<{ item_id: string }>; transaction_id: string },
  pendingCheckout: PendingSubscriptionCheckout,
  latestPaymentPeriod: string,
) =>
  Boolean(pendingCheckout.latestTransactionIdBeforeCheckout) &&
  latestPaymentInfo.transaction_id !== pendingCheckout.latestTransactionIdBeforeCheckout &&
  latestPaymentInfo.items[0]?.item_id === pendingCheckout.productId &&
  getLatestPaymentInterval(latestPaymentPeriod) === pendingCheckout.interval;

export const useHandlePaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: user } = useCurrentUser();
  const { hasActiveSubscription, planName } = useUserBalanceStore();

  const periodLabel = getPlanPeriodLabel(user?.subscription?.plan.interval);

  const removeSearchParam = (paramName: string) => {
    const nextSearchParams = new URLSearchParams(searchParams);

    nextSearchParams.delete(paramName);
    setSearchParams(nextSearchParams);
  };

  const finalizePaymentReturn = (options: { navigateHome: boolean; navigateTo?: string; stripPriceId: boolean }) => {
    const nextSearchParams = new URLSearchParams(searchParams);

    nextSearchParams.delete(PAYMENT_SUCCESSFUL_PARAM);
    stripStripeRedirectParams(nextSearchParams);

    if (options.stripPriceId) nextSearchParams.delete(PRICE_ID_PARAM);

    // A purchase started from a recording paywall goes back to that recording, which repeats the blocked action.
    if (options.navigateTo) {
      navigate(options.navigateTo, { replace: true });
      return;
    }

    setSearchParams(nextSearchParams);

    if (options.navigateHome)
      // Preserve unrelated params, but drop success-only markers.
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      navigate(PATHS.homepage + (nextSearchParams.toString() ? `?${nextSearchParams}` : ''), { replace: true });
  };

  const trackSubscriptionSuccess = (latestPaymentInfo: IGetLatestPaymentInfoResponse, subscription: string) => {
    MixpanelEvents.paymentSuccessfulSubscription({
      hasActiveSubscription,
      subscription,
    });
    GTMEvents.purchase(latestPaymentInfo);
    clearPendingSubscriptionCheckout();
    clearCheckoutEntryPoint();
  };

  useExecuteOneTime(async () => {
    const hasPaymentSuccessfulParam = searchParams.has(PAYMENT_SUCCESSFUL_PARAM);
    const hasSubscriptionCancelParam = searchParams.has(PAYMENT_SUBSCRIPTION_CANCELED_PARAM);
    const hasCreditsCancelParam = searchParams.has(PAYMENT_CREDITS_CANCELED_PARAM);
    const redirectStatus = searchParams.get('redirect_status');
    const pendingSubscriptionCheckout = readPendingSubscriptionCheckout();
    // Read before the success handling forgets it: it tells whether a recording paywall started this checkout.
    const checkoutEntryPoint = readCheckoutEntryPoint();

    const validPendingSubscriptionCheckout =
      pendingSubscriptionCheckout && !isPendingSubscriptionCheckoutExpired(pendingSubscriptionCheckout)
        ? pendingSubscriptionCheckout
        : null;

    if (pendingSubscriptionCheckout && !validPendingSubscriptionCheckout) clearPendingSubscriptionCheckout();

    if (hasSubscriptionCancelParam) {
      const recordingReturn = takeCanceledRecordingCheckoutReturn(checkoutEntryPoint);

      clearPendingSubscriptionCheckout();
      clearCheckoutEntryPoint();
      MixpanelEvents.paymentSubscriptionCanceled();

      // Back to the page whose recording paywall started the checkout (the recording, or the list/Home it was on).
      if (recordingReturn) {
        navigate(getRecordingCheckoutBackPath(recordingReturn), { replace: true });
        return;
      }

      removeSearchParam(PAYMENT_SUBSCRIPTION_CANCELED_PARAM);
      return;
    }

    if (hasCreditsCancelParam) {
      MixpanelEvents.paymentCreditsCanceled();
      removeSearchParam(PAYMENT_CREDITS_CANCELED_PARAM);
      return;
    }

    if (!hasPaymentSuccessfulParam) {
      if (!validPendingSubscriptionCheckout) return;

      const latestPaymentInfo = await API.getLatestPaymentInfo().catch(() => null);

      if (!latestPaymentInfo) return;

      const { name, latestPaymentPeriod } = getLatestPaymentData(latestPaymentInfo);

      if (
        !isSubscriptionPayment(latestPaymentPeriod) ||
        !matchesPendingSubscriptionCheckout(latestPaymentInfo, validPendingSubscriptionCheckout, latestPaymentPeriod)
      )
        return;

      trackSubscriptionSuccess(latestPaymentInfo, `${name} ${latestPaymentPeriod}`);
      return;
    }

    if (redirectStatus && redirectStatus !== 'succeeded') {
      clearPendingSubscriptionCheckout();
      clearCheckoutEntryPoint();
      clearRecordingCheckoutReturn();
      finalizePaymentReturn({ navigateHome: false, stripPriceId: false });
      return;
    }

    if (user?.credits)
      MixpanelEvents.paymentSuccessful({
        minutes: user.credits.minutes.total,
      });

    const latestPaymentInfo = await API.getLatestPaymentInfo().catch(() => null);

    if (!latestPaymentInfo) {
      const nextSearchParams = new URLSearchParams(searchParams);

      stripStripeRedirectParams(nextSearchParams);
      setSearchParams(nextSearchParams);

      return;
    }

    const { name, latestPaymentPeriod, quantity, range } = getLatestPaymentData(latestPaymentInfo);

    if (isSubscriptionPayment(latestPaymentPeriod)) {
      trackSubscriptionSuccess(latestPaymentInfo, `${name} ${latestPaymentPeriod}`);
    } else {
      clearCheckoutEntryPoint();
      MixpanelEvents.paymentSuccessfulMinutes({
        hasActiveSubscription,
        minutes: quantity,
        minutes_range: range,
        subscription: `${planName} ${periodLabel}`,
      });
    }

    const recordingReturn = markRecordingCheckoutReturnPaid(checkoutEntryPoint);

    finalizePaymentReturn({ navigateHome: true, navigateTo: recordingReturn?.path, stripPriceId: true });
  });
};
